// Invoice detail modal — line items, tax breakdown, payment recording, Issue
// and PDF. Shared by the Invoices page and the client's Invoices tab so both
// offer the identical flow.
import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const STATUS_TONE = {
  draft: "text-muted-foreground border-muted-foreground/40",
  issued: "text-blue-600 border-blue-500/50 dark:text-blue-400",
  partially_paid: "text-amber-600 border-amber-500/50 dark:text-amber-400",
  paid: "text-emerald-600 border-emerald-500/50 dark:text-emerald-400",
  overdue: "text-red-600 border-red-500/50 dark:text-red-400",
  cancelled: "text-muted-foreground line-through",
};

export const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export async function downloadInvoicePdf(invoice) {
  const res = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${String(invoice.invoice_number).replace(/\//g, "-")}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * @param invoiceId  id to load — always re-fetched so the modal shows fresh state
 * @param clientName label under the invoice number
 * @param onClose    close handler
 * @param onChange   called after issue/payment so the caller can refresh its list
 */
export default function InvoiceDetailDialog({ invoiceId, clientName, onClose, onChange }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => api.get(`/invoices/${invoiceId}`).then(({ data }) => setInv(data)),
    [invoiceId],
  );
  useEffect(() => { load(); }, [load]);

  const issue = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${inv.id}/issue`);
      toast.success(`Invoice ${inv.invoice_number} issued`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to issue invoice"));
    } finally {
      setBusy(false);
    }
  };

  const pdf = async () => {
    setBusy(true);
    try {
      await downloadInvoicePdf(inv);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-detail-dialog">
        {!inv ? (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 size={16} className="animate-spin inline mr-2" />Loading…
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-mono">
                {inv.invoice_number}
                <Badge variant="outline" className={cn("text-[10px] capitalize", STATUS_TONE[inv.invoice_status])}>
                  {inv.invoice_status.replace("_", " ")}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {clientName} · {formatDate(inv.invoice_date, "dd MMM yyyy")}
              </DialogDescription>
            </DialogHeader>

            <Card className="p-3 border-border shadow-none">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Line items</div>
              <div className="space-y-1.5">
                {(inv.lines || []).map((l, i) => (
                  <div key={l.id || `line-${i}`} className="flex items-start gap-3 text-sm">
                    <div className="flex-1">
                      <div className="font-medium">{l.description}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {l.hsn_sac} · {l.quantity} × {money(l.unit_price)} · {l.gst_rate}% GST
                      </div>
                    </div>
                    <div className="font-mono text-sm">{money(l.total)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border mt-3 pt-3 text-sm space-y-1">
                <Row label="Subtotal" value={money(inv.subtotal)} />
                {(inv.cgst_total || inv.sgst_total) ? (
                  <>
                    <Row label="CGST" value={money(inv.cgst_total)} />
                    <Row label="SGST" value={money(inv.sgst_total)} />
                  </>
                ) : null}
                {inv.igst_total ? <Row label="IGST" value={money(inv.igst_total)} /> : null}
                <Row label="Grand total" value={money(inv.grand_total)} bold />
                {inv.amount_paid ? <Row label="Paid" value={money(inv.amount_paid)} /> : null}
                {inv.amount_due ? <Row label="Due" value={money(inv.amount_due)} bold /> : null}
              </div>
            </Card>

            <PaymentPanel
              invoice={inv}
              onChange={async () => { await load(); onChange?.(); }}
            />

            <DialogFooter>
              {inv.invoice_status === "draft" && (
                <Button onClick={issue} disabled={busy} data-testid="invoice-issue">
                  {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />}
                  Issue
                </Button>
              )}
              <Button variant="outline" onClick={pdf} disabled={busy} data-testid="invoice-pdf">
                <Download size={13} className="mr-1.5" /> PDF
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-semibold" : "text-muted-foreground"}>{label}</span>
      <span className={cn("font-mono", bold && "font-semibold")}>{value}</span>
    </div>
  );
}

function PaymentPanel({ invoice, onChange }) {
  const [amount, setAmount] = useState(invoice.amount_due || 0);
  const [method, setMethod] = useState("bank");
  const [ref, setRef] = useState("");
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invoices/${invoice.id}/payments`).then(({ data }) => setPayments(data)).catch(() => {});
  }, [invoice.id, invoice.amount_paid]);

  // Keep the amount box in step with what is actually outstanding after a part payment.
  useEffect(() => { setAmount(invoice.amount_due || 0); }, [invoice.amount_due]);

  if (!["issued", "partially_paid", "overdue", "paid"].includes(invoice.invoice_status)) return null;

  const pay = async () => {
    if (Number(amount) <= 0) return;
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, {
        amount: Number(amount),
        method,
        paid_on: new Date().toISOString().slice(0, 10),
        reference: ref,
      });
      toast.success("Payment recorded");
      setRef("");
      onChange();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to record payment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 border-border shadow-none">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Payments</div>
      {payments.length === 0 && <div className="text-xs text-muted-foreground mb-2">No payments recorded.</div>}
      {payments.map((p) => (
        <div key={p.id} className="flex justify-between text-sm py-1 border-b border-border/60 last:border-0">
          <span className="capitalize">{p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
          <span className="font-mono">{money(p.amount)}</span>
        </div>
      ))}
      {invoice.invoice_status !== "paid" && (
        <div className="flex gap-2 mt-2">
          <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 w-32" data-testid="pay-amount" />
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-8 w-28" data-testid="pay-method"><SelectValue /></SelectTrigger>
            <SelectContent>{["bank", "upi", "cash", "cheque", "card"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} className="h-8 flex-1" data-testid="pay-ref" />
          <Button size="sm" onClick={pay} disabled={busy} data-testid="pay-submit">
            {busy && <Loader2 size={12} className="animate-spin mr-1.5" />}Record
          </Button>
        </div>
      )}
    </Card>
  );
}
